import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BulkActionJobDto } from 'lib';
import { TableModule } from 'primeng/table';
import { FileSelectEvent, FileUpload } from 'primeng/fileupload';
import { CommonModule, NgIf } from '@angular/common';
import { Select } from 'primeng/select';
import { BulkActionTypeEnum } from '../../interfaces/bulk-action-type-enum';
import { BulkActionJobService } from '../../services/bulk-action-job.service';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { EmptyStateComponent, FormatDateArrayPipe, getEnumKeyFromValue, PaginatedSearchFilter, PaginationComponent } from 'lib';
import { LazyLoadEvent } from 'primeng/api';
import { saveAs } from 'file-saver';

@Component({
    selector: 'mmoga-upload-csv',
    imports: [
        EmptyStateComponent,
        ReactiveFormsModule,
        TableModule, CommonModule,
        FileUpload,
        NgIf,
        Select,
        FormsModule,
        FormatDateArrayPipe,
        PaginationComponent
    ],
    templateUrl: './upload-csv.component.html',
    standalone: true,
    styleUrl: './upload-csv.component.scss'
})
export class UploadCsvComponent implements OnInit {
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly bulkActionJobService = inject(BulkActionJobService);
    private readonly toastrService = inject(ToastrService);


    @ViewChild('fileUpload') fileUpload!: FileUpload;
    @ViewChild('pagination') pagination!: PaginationComponent;

    enterpriseId = 0;
    bulkActionJobs: BulkActionJobDto[] = [];
    bulkActionTypes: string[] = Object.values(BulkActionTypeEnum);
    selectedActionType: string | undefined;
    selectedFile: File | undefined;
    uploading = false;
    currentPage = 1;
    itemsPerPage = 10;
    totalItems = 0;
    sortBy = 'fileName';
    sortOrder = 'ASC';

    ngOnInit(): void {
        this.activatedRoute.parent?.params.subscribe(params => {
            this.enterpriseId = params[ 'id' ];
            this.loadJobs();
        });
    }

    onFileSelect($event: FileSelectEvent): void {
        this.selectedFile = $event.files[ 0 ];
    }

    isUploadDisabled(): boolean {
        return !(this.selectedFile && this.selectedActionType) || this.uploading;
    }

    isExecuteDisabled(): boolean {
        return this.bulkActionJobs.some(job => job.status === 'PROCESSING') || this.bulkActionJobs.length === 0;
    }

    loadJobs(): void {
        this.bulkActionJobService.getByEnterpriseId(this.enterpriseId, this.getPaginatedSearchFilter())
            .subscribe({
                next: (data) => {
                    this.bulkActionJobs = data.content;
                    this.totalItems = data.totalElements;
                    this.pagination = new PaginationComponent();
                    this.pagination.totalItems = this.totalItems;
                    if (data.content.length === 0) {
                        this.currentPage = 1;
                    }
                    this.pagination?.ngOnInit();
                }
            });
    }

    uploadFile(): void {
        const actionType = getEnumKeyFromValue(BulkActionTypeEnum, this.selectedActionType as string);
        if (!this.uploading && actionType && this.selectedFile instanceof File) {
            this.uploading = true;

            const formData = new FormData();
            formData.append('actionType', actionType);
            formData.append('file', this.selectedFile);

            this.bulkActionJobService.uploadByEnterprise(this.enterpriseId, formData)
                .subscribe({
                    next: (entryCount) => {
                        this.toastrService.success(`Successfully loaded ${this.selectedFile?.name} with ${entryCount} entries.`);
                    },
                    error: () => {
                        this.toastrService.error(`Failed to load ${this.selectedFile?.name}. See logs for details.`);
                        this.uploading = false;
                    },
                    complete: () => {
                        this.uploading = false;
                        this.clearSelection();
                        this.loadJobs();
                    }
                });
        }
    }

    executeJob(): void {
        this.bulkActionJobService.executeByEnterprise(this.enterpriseId).subscribe({
            error: () => {
                this.toastrService.error(`Failed to execute job. See logs for details.`);
            },
            complete: () => {
                this.toastrService.success('Successfully executed job. Please reload page for updates.');
                this.loadJobs();
            }
        });
    }

    clearSelection(): void {
        this.fileUpload.clear();
        this.selectedFile = undefined;
        this.selectedActionType = undefined;
    }

    downloadOutput(fileName: string): void {
        this.bulkActionJobService.downloadCsv(this.enterpriseId, fileName).subscribe({
            next: (data) => {
                if (data) {
                    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
                    saveAs(blob, fileName);
                    this.toastrService.success(`Successfully downloaded ${fileName}.`);
                } else {
                    this.toastrService.error('No data available for download. Please check if file has completed processing.');
                }
            }, error: () => {
                this.toastrService.error(`Failed to download csv for ${fileName}. See logs for details.`);
            }
        });
    }

    customSort(event: LazyLoadEvent): void {
        this.sortBy = event.sortField ? event.sortField : 'fileName';
        this.sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';
        this.loadJobs();
    }

    onSelectItemsPerPage(itemsPerPage: number): void {
        this.itemsPerPage = itemsPerPage;
    }

    onChangeCurrentPage(currentPage: number): void {
        this.currentPage = currentPage;
        this.loadJobs();
    }

    getPaginatedSearchFilter(): PaginatedSearchFilter {
        return {
            page: this.currentPage - 1,
            itemsPerPage: this.itemsPerPage,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder
        } as PaginatedSearchFilter;
    }

}
